/**
 * Phase 8 · Cluster C-43 — Sourcing connector tools registered with
 * the agent tool registry.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * Three tools, one per connector. Each tool's invoke() delegates to the
 * matching `SourcingConnector` implementation (./connectors/*) and returns
 * `{ candidates: SourcingResult[] }`. The shape is shared on purpose —
 * the runner can deduplicate + merge the three result sets into one
 * candidate card list for the user without branching per connector.
 *
 * Error handling: connector errors propagate out of invoke(). The
 * registry's dispatch() captures them, records `ok=false` in the audit
 * row with the error message, and re-throws. The agent runner turns that
 * into a tool_result event with `ok: false` and a short friendly summary
 * — the user sees "LinkedIn not configured" rather than a stack trace.
 */
import { z } from 'zod';
import { toolRegistry, type AgentTool, type AgentToolPermission } from './registry';
import { internalConnector } from './connectors/internal';
import { linkedinConnector } from './connectors/linkedin';
import { naukriConnector } from './connectors/naukri';
import { githubConnector } from './connectors/github';
import { gitlabConnector } from './connectors/gitlab';
import { discordConnector } from './connectors/discord';
import {
  DEFAULT_SOURCING_LIMIT,
  MAX_SOURCING_LIMIT,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './connectors/types';

// ── Shared zod schemas ─────────────────────────────────────────────────

const sourcingQuerySchema = z.object({
  keywords: z.array(z.string()).default([]),
  location: z.string().optional(),
  experienceYearsMin: z.number().nonnegative().optional(),
  experienceYearsMax: z.number().nonnegative().optional(),
  skills: z.array(z.string()).optional(),
  limit: z
    .number()
    .int()
    .positive()
    .max(MAX_SOURCING_LIMIT)
    .optional(),
});

const sourcingResultSchema = z.object({
  externalId: z.string(),
  name: z.string(),
  headline: z.string().optional(),
  locationText: z.string().optional(),
  profileUrl: z.string(),
  avatarUrl: z.string().optional(),
  signals: z.record(z.unknown()),
  source: z.enum(['linkedin', 'naukri', 'github', 'gitlab', 'discord', 'internal']),
});

const sourcingResultBundleSchema = z.object({
  candidates: z.array(sourcingResultSchema),
});

type SourcingArgs = z.infer<typeof sourcingQuerySchema>;
type SourcingResultBundle = z.infer<typeof sourcingResultBundleSchema>;

/**
 * Build a tool definition from a connector. We factor this out so the
 * three registrations below stay copy-free and so future connectors
 * (github/gitlab/discord in C-44) land with a one-line addition.
 */
const SOURCING_PERMISSIONS: AgentToolPermission[] = ['sourcing:read'];

function defineSourcingTool(
  name: string,
  connector: SourcingConnector,
  description: string,
): AgentTool<SourcingArgs, SourcingResultBundle> {
  return {
    name,
    description,
    argsSchema: sourcingQuerySchema,
    resultSchema: sourcingResultBundleSchema,
    permissions: SOURCING_PERMISSIONS,
    async invoke(ctx, args): Promise<SourcingResultBundle> {
      if (!ctx.orgId) return { candidates: [] };
      const query: SourcingQuery = {
        keywords: args.keywords ?? [],
        location: args.location,
        experienceYearsMin: args.experienceYearsMin,
        experienceYearsMax: args.experienceYearsMax,
        skills: args.skills,
        limit: args.limit ?? DEFAULT_SOURCING_LIMIT,
      };
      const results: SourcingResult[] = await connector.search(query, ctx.orgId);
      return { candidates: results };
    },
  };
}

// ── source.internal.candidates ────────────────────────────────────────
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.internal.candidates',
    internalConnector,
    "Search the org's own candidate pool with a sourcing-style query (keywords, location, experience range, skills). Always available — no credentials required.",
  ),
);

// ── source.linkedin.search ────────────────────────────────────────────
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.linkedin.search',
    linkedinConnector,
    'Find candidates on LinkedIn (Sales Navigator / Recruiter) matching the sourcing query. Requires a configured LinkedIn connector (OAuth or session cookie). Read-only.',
  ),
);

// ── source.naukri.search ──────────────────────────────────────────────
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.naukri.search',
    naukriConnector,
    'Find candidates on Naukri matching the sourcing query. Requires a configured Naukri connector (API key). Read-only.',
  ),
);

// ── source.github.search ──────────────────────────────────────────────
// C-44 — uses GitHub's public people-search API; works unauthenticated,
// a configured personal access token lifts the rate ceiling (path is
// Edge-Function-gated for security).
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.github.search',
    githubConnector,
    'Find candidates on GitHub by keywords, skills (languages), and location. Works without a token; configured token lifts the rate ceiling. Read-only, no repo hydration at search time.',
  ),
);

// ── source.gitlab.search ──────────────────────────────────────────────
// C-44 — GitLab.com public user search, parallel to GitHub. Skills /
// location are applied as post-filters because GitLab's `/users` search
// only accepts a single free-text `search` param server-side.
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.gitlab.search',
    gitlabConnector,
    'Find candidates on GitLab.com. Works without a token; configured token lifts the rate ceiling. Read-only.',
  ),
);

// ── source.discord.search ─────────────────────────────────────────────
// C-44 — scans a single Discord guild's members via the REST API
// (routed through the Edge Function proxy — Discord blocks CORS on
// authed bot calls). Requires a bot token + `config_json.guildId`.
toolRegistry.register<SourcingArgs, SourcingResultBundle>(
  defineSourcingTool(
    'source.discord.search',
    discordConnector,
    'Find candidates in a configured Discord guild by filtering members against the sourcing keywords. Requires a bot token + guild id. Read-only.',
  ),
);
